/**
 * useAttributionCapture — mount this on any public landing page.
 *
 * On mount:
 *   1. Reads `?ref=&via=` from the URL
 *   2. If present, POSTs to `/api/attribution/click` to log the visit
 *   3. Stashes the returned session_token in sessionStorage under
 *      `af_attribution` so the register flow can attach it to the signup
 *
 * On registration, RegisterPage passes the token in the register payload
 * and the backend fires `/api/attribution/convert` to credit the referrer.
 *
 * Silent-fail by design — attribution is best-effort telemetry and must
 * never block the user experience or throw a visible error.
 */
import { useEffect } from "react";
import { api } from "@/lib/api";

const STORAGE_KEY = "af_attribution";
const VIA_ALLOWED = new Set(["deal_won", "referral", "affiliate"]);

export default function useAttributionCapture() {
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const ref = (params.get("ref") || "").trim();
      const via = (params.get("via") || "deal_won").trim();
      if (!ref || !VIA_ALLOWED.has(via)) return;
      // Skip if we've already captured a session for this ref this browser.
      const existing = readStoredAttribution();
      if (existing && existing.ref === ref) return;
      api.post("/attribution/click", {
        ref,
        via,
        source_path: window.location.pathname,
        referer: (typeof document !== "undefined" && document.referrer) || "",
      }).then((r) => {
        const token = r?.data?.session_token;
        if (!token) return;
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify({
          session_token: token,
          ref, via,
          captured_at: new Date().toISOString(),
        }));
      }).catch(() => { /* silent */ });
    } catch { /* silent */ }
  }, []);
}

// Read the stashed attribution — used by the register flow.
export function readStoredAttribution() {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch { return null; }
}

// Clear after successful signup so a subsequent session doesn't leak state.
export function clearStoredAttribution() {
  try { sessionStorage.removeItem(STORAGE_KEY); } catch { /* silent */ }
}
